import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ApiContext } from '@api/api';
import { PickList, PickListChangeParams } from 'primereact/picklist';
import { InputText } from 'primereact/inputtext';
import { LovGetPermissionResponse, PermissionTypesEnum } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';

interface ComponentProps {
  value: PermissionTypesEnum[] | string[];
  onChange: (selectedPermission: PermissionTypesEnum[]) => void;
}

const InputPickPermissions: React.FC<ComponentProps> = ({ value, onChange }) => {
  const { lovGetPermissions } = useContext(ApiContext);
  const { tr } = useTranslationLgs();

  const [sourcePermissions, setSourcePermissions] = useState<LovGetPermissionResponse>();
  const [sourceFilter, setSourceFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');

  useEffect(() => {
    const ac = new AbortController();
    lovGetPermissions(d => {
      if (!ac.signal.aborted) {
        setSourcePermissions(d);
      }
    });
    return () => {
      ac.abort();
    };
  }, []);

  const allNames = useMemo(() => sourcePermissions?.permissions.map(t => t.name) ?? [], [sourcePermissions]);
  const selected = (value ?? []) as string[];

  const matches = (name: string, filter: string) => name.toLowerCase().includes(filter.trim().toLowerCase());

  const source = allNames.filter(name => !selected.includes(PermissionTypesEnum[name]) && matches(name, sourceFilter));
  const target = allNames.filter(name => selected.includes(PermissionTypesEnum[name]) && matches(name, targetFilter));

  // Reconstruct the full selection from the move event. Items currently filtered
  // out of a list are not present in event.source/target, so they keep their side.
  const onChangeInternal = (event: PickListChangeParams) => {
    const sourceNames: string[] = event.source ?? [];
    const targetNames: string[] = event.target ?? [];
    const visible = new Set<string>([...sourceNames, ...targetNames]);
    const targetSet = new Set<string>(targetNames);
    const next = new Set<string>(selected);
    allNames.forEach(name => {
      if (!visible.has(name)) return; // filtered-out item keeps its current side
      if (targetSet.has(name)) next.add(PermissionTypesEnum[name]);
      else next.delete(PermissionTypesEnum[name]);
    });
    onChange(Array.from(next) as PermissionTypesEnum[]);
  };

  const filterHeader = (label: string, filter: string, setFilter: (v: string) => void) => (
    <div className="flex flex-col gap-2">
      <span>{label}</span>
      <InputText
        value={filter}
        onChange={e => setFilter(e.target.value)}
        onClick={e => e.stopPropagation()}
        placeholder={tr('InputPickPermissions.search', 'Search permission')}
        className="w-full"
      />
    </div>
  );

  return (
    <PickList
      source={source}
      target={target}
      onChange={onChangeInternal}
      sourceHeader={filterHeader(tr('InputPickPermissions.available', 'Available'), sourceFilter, setSourceFilter)}
      targetHeader={filterHeader(tr('InputPickPermissions.selected', 'Selected'), targetFilter, setTargetFilter)}
      showSourceControls={false}
      showTargetControls={false}
    />
  );
};

export default InputPickPermissions;